import { searchDestinationDisp, searchDestinations } from "../api/destinations";
import { IDestination } from "../models/destination";
import { searchParamsInit } from "../models/searchParams";
import { Roles, UserData } from "../models/userData";

const pending = new Map<
    string,
    { request: Promise<IDestination[]>; trailing?: Promise<IDestination[]> }
>();
type DestinationUser = Pick<UserData, "roles" | "companyId" | "token">;

export function canRefreshDestinations(user: DestinationUser) {
    return (
        user.roles.includes(Roles.LOCKS_AND_DOCKS_DISPATCHER) ||
        user.roles.includes(Roles.PASSENGER_CABIN_CRUISES_DISPATCHER) ||
        user.roles.includes(Roles.PASSENGER_CABIN_CRUISES_DISPATCHER_ADMIN)
    );
}

/** One authorized metadata request for concurrent refresh consumers, including inactive destinations. */
export function fetchDestinationMetadata(
    user: DestinationUser,
    fresh = false,
): Promise<IDestination[]> {
    const docks = user.roles.includes(Roles.LOCKS_AND_DOCKS_DISPATCHER);
    if (!canRefreshDestinations(user))
        return Promise.reject(new Error("Destination metadata unavailable for this role"));
    const key = JSON.stringify([docks, user.companyId, user.token]);
    const existing = pending.get(key);
    if (existing) {
        if (!fresh) return existing.request;
        // A change event or Save preflight must not reuse a request begun before the change.
        existing.trailing ??= existing.request
            .catch(() => undefined)
            .then(() => fetchDestinationMetadata(user));
        return existing.trailing;
    }
    const request = (
        docks
            ? searchDestinations(searchParamsInit)
            : searchDestinationDisp({ ...searchParamsInit, filter: { companyId: user.companyId } })
    )
        .then(response => response.data.rows ?? [])
        .finally(() => pending.delete(key));
    pending.set(key, { request });
    return request;
}
